import cached from 'utils/cached';
import pendingPromise from 'utils/pendingPromise';
import { normalizeCMSMovieData } from './cmsDataNormalizer';

const qs = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
export const previewMode = qs.has('cms-preview') && window.parent !== window;
export const fullPage = qs.has('fullpage');

let DATA_DOMAIN =
	__IS_LOCAL__ || __IS_DEMO__
		? (qs.has('prod') ? 'https://www.focusfeaturesfilms.co.uk' : 'https://focus.hubsite.dev.cmspow.com')
		: (typeof window !== 'undefined' ? location.origin : 'https://www.focusfeaturesfilms.co.uk');

let previewDataWaitlist = {};
async function waitForPreviewData(name) {
	let promise = pendingPromise();
	previewDataWaitlist[name] ||= [];
	previewDataWaitlist[name].push(promise);
	return promise;
}

if (previewMode) {
	window.addEventListener('message', e => {
		let message = e.data;
		if (message?.type === 'data' && previewDataWaitlist[message.name]) {
			previewDataWaitlist[message.name].forEach(p => p.resolve(message.data));
			previewDataWaitlist[message.name] = [];
		}
		// Clear navigation cache when CMS signals it has changed
		if (message?.type === 'invalidateCache' && message?.name === 'navigation') {
			getData.reset(); // Clear entire cache to ensure fresh data
		}
	}, false);
}

const cleanType = type => type === 'movie' ? 'movie' : 'section';

function getUrl(name, type) {
	const transform = (name, type) => {
		if (type === 'movie') {
			if (name === 'all') {
				return 'cms/movies/all/'; // endpoint for fetching all movies
			}

			return `cms/movies/slugs/${name}.json`; // endpoint for fetching individual movie by slug
		}

		return `cms/${type}s/${name}.json`; // endpoint for fetching sections and other types of data
	};

	const url = transform(name, type);
	return cacheBustedUrl(url); // Add cache buster to ensure fresh data in preview mode, but allow caching in production for performance
}

async function getDataRaw(name, type = 'section') {
	type = cleanType(type);

	if (previewMode && window.parent !== window) {
		window.parent.postMessage({ type: 'requestData', name }, '*');
		let data = await waitForPreviewData(name);
		if (data !== null) {
			// Normalize CMS movie data to flat structure
			if (type === 'movie') {
				return normalizeCMSMovieData(data);
			}
			return data;
		}
	}

	try {
		const url = getUrl(name, type);
		const res = await fetch(new URL(url, DATA_DOMAIN));

		if (!res.ok) {
			throw new Error(`Data unavailable for ${name}`);
		}

		const data = await res.json();

		// Normalize CMS movie data to flat structure
		if (type === 'movie' && data) {
			if (Array.isArray(data)) return data.map(normalizeCMSMovieData);
			return normalizeCMSMovieData(data);
		}

		return data;
	} catch {}

	return null;
}

export function cacheBustedUrl(url) {
	if (!previewMode) return url; // only add cache buster in preview mode to ensure we get fresh data, but allow caching in production for performance

	// prevent caching in preview mode to ensure fresh data
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}_cb=${Date.now()}`;
}

export const getData = cached(getDataRaw, (name, type) => `${cleanType(type)}-${name}`);
